import { useEffect, useRef, useState } from "react";
import { I } from "./Icons";

export interface ChatContext {
  courseId?: string;
  courseTitle?: string;
  courseDesc?: string;
  stepTitle?: string;
  stepSub?: string;
  stepEyebrow?: string;
  stepKind?: string;
  screen?: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface Props {
  context: ChatContext;
}

export function Chatbot({ context }: Props) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, open, pending]);

  async function send() {
    const text = input.trim();
    if (!text || pending) return;
    const next: Message[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setPending(true);
    setError(null);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ messages: next, context }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong");
    } finally {
      setPending(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <>
      {!open && (
        <button
          className="gf-chat__fab"
          onClick={() => setOpen(true)}
          aria-label="Open git tutor"
        >
          <I.Sparkles size={18} />
          <span>Ask the tutor</span>
        </button>
      )}
      {open && (
        <div className="gf-chat" role="dialog" aria-label="Git tutor chat">
          <div className="gf-chat__head">
            <div className="gf-chat__head-l">
              <div className="gf-chat__avatar">
                <I.Sparkles size={14} />
              </div>
              <div>
                <div className="gf-chat__title">Git tutor</div>
                <div className="gf-chat__sub">
                  {context.courseTitle ?? "Git Fit"}
                </div>
              </div>
            </div>
            <button
              className="gf-chat__close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="gf-chat__body" ref={bodyRef}>
            {messages.length === 0 && (
              <div className="gf-chat__bubble gf-chat__bubble--bot">
                Hi! I'm your git tutor. I know what you're looking at on this
                page — what's tripping you up?
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={
                  "gf-chat__bubble " +
                  (m.role === "user"
                    ? "gf-chat__bubble--me"
                    : "gf-chat__bubble--bot")
                }
              >
                {m.content}
              </div>
            ))}
            {pending && (
              <div className="gf-chat__bubble gf-chat__bubble--bot gf-chat__bubble--typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
            {error && <div className="gf-chat__err">{error}</div>}
          </div>

          <div className="gf-chat__input">
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about this step…"
              disabled={pending}
            />
            <button
              className="gf-btn gf-btn--gradient"
              onClick={send}
              disabled={pending || !input.trim()}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}
